import React, { useContext, useState } from "react";

import { kebabCase, omit } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import styled from "styled-components";

import Loading from "../Loading";

import * as Breadcrumbs from "~/components/Breadcrumbs";
import * as Grid from "~/components/Grid";
import { ArrowLeftIcon } from "~/components/icons/Arrow";
import Spacer, { Axis } from "~/components/Spacer";
import {
  DASHBOARD_DIRECTORY_BASEPATH,
  PUBLIC_DIRECTORY_BASEPATH,
  BackButtonContext,
} from "~/contexts/BackButtonContext/BackButtonContext";
import { useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import VaspCard from "~/templates/network/components/VaspSections/cards/VaspCard";
import { VASPHeaderLogo, Title } from "~/templates/network/components/VaspSections/cards/VaspCard/styles";
import { VASP } from "~/types/vaspid";

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  column-gap: 24px;
`;

type VASPListSubsidiariesProps = {
  gatewayVaspDid: string;
  includeVaspReview?: boolean;
};

const BackButtonToVaspList = () => {
  const router = useRouter();
  const basePath = router.pathname.includes("dashboard") ? DASHBOARD_DIRECTORY_BASEPATH : PUBLIC_DIRECTORY_BASEPATH;
  const params = new URLSearchParams(omit(router.query, ["subsidiariesOf"]) as { [key: string]: string }).toString();
  const backUrl = `${basePath}?${params}`;

  return (
    <Breadcrumbs.Container>
      <Breadcrumbs.Content>
        <Breadcrumbs.Item>
          <Link href={backUrl} passHref replace>
            <Breadcrumbs.ItemContent>
              <Breadcrumbs.ItemContentIcon>
                <ArrowLeftIcon />
              </Breadcrumbs.ItemContentIcon>
              <Breadcrumbs.ItemContentText>Back</Breadcrumbs.ItemContentText>
            </Breadcrumbs.ItemContent>
          </Link>
        </Breadcrumbs.Item>
      </Breadcrumbs.Content>
    </Breadcrumbs.Container>
  );
};

export default function VASPListSubsidiaries({
  gatewayVaspDid,
  includeVaspReview = false,
  ...props
}: VASPListSubsidiariesProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const { storeSearchParams } = useContext(BackButtonContext);
  const currentVaspDID = useCurrentViewerVaspDID();

  const { data: gatewayVaspInfo, isLoading: isLoadingGateway, error } = useTfSimpleVaspInfo(
    gatewayVaspDid,
    [
      "did",
      "name",
      "logo",
      "verificationStatus",
      "jurisdictions",
      "division",
      "subsidiaries",
      "hasAdmin",
      ...(includeVaspReview ? ["reviewValue", "reviewDate"] : []),
    ],
    currentVaspDID,
  );

  const vaspDoesNotHaveSubsidiaries =
    gatewayVaspInfo && (!gatewayVaspInfo.subsidiaries || !gatewayVaspInfo.subsidiaries.length);
  if (error || vaspDoesNotHaveSubsidiaries) {
    toast.error(`There was a problem when loading vasp ${gatewayVaspDid}`);
    router.push(`/network/search`); //go back to network
    return null;
  }

  if (!gatewayVaspInfo) {
    return null; //shouldnt happen, just to make ts happy
  }

  const subsidiaryVasps: VASP[] = gatewayVaspInfo?.subsidiaries || []; //make ts happy

  const handleVaspCardClicked = (vasp: VASP) => {
    setIsLoading(true);
    storeSearchParams();
    const vaspDetailsHref = router.pathname.includes("dashboard")
      ? `/dashboard/companies/${vasp.did}`
      : `/network/${vasp.did}/${kebabCase(vasp.name || undefined)}`;
    router.push(vaspDetailsHref);
  };

  return (
    <div style={{ width: "100%" }}>
      <BackButtonToVaspList />

      <HeaderContainer>
        <VASPHeaderLogo
          src={gatewayVaspInfo.logo ? gatewayVaspInfo.logo : "/assets/vasp-fallback.png"}
          className="localize-ignore"
        />
        <Title className="localize-ignore">{gatewayVaspInfo.name}</Title>
      </HeaderContainer>
      <Spacer axis={Axis.VERTICAL} size={30} />

      {(isLoadingGateway || isLoading) && <Loading />}

      <Grid.Columns as="ul" multiline {...props}>
        {subsidiaryVasps.map((vasp) => (
          <Grid.Column as="li" key={vasp.did} mobile={12}>
            <VaspCard vasp={vasp} onClick={() => handleVaspCardClicked(vasp)} includeVaspReview={includeVaspReview} />
          </Grid.Column>
        ))}
      </Grid.Columns>
    </div>
  );
}
